import { Box, Tab, Tabs, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import LandmarkPanel from '~/components/Landmarks/LandmarkPanel';
import { useResetDeformationOverlays } from '~/hooks/viewer/useResetDeformationOverlays';
import {
    replanningRegisterTabState,
    replanningRunOutputsState,
    replanningVisibleRegisterTabState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import PropagationForm from './PropagationForm';
import RegisterRunSummary from './RegisterRunSummary';
import RegisterVisualizationPanel from './RegisterVisualizationPanel';
import { useRunLandmarkComparison } from './useRunLandmarkComparison';
import { useSelectRunRegistration } from './useSelectRunRegistration';

export default function RegisterStage() {
    const intl = useIntl();
    const selectedTool = useRecoilValue(selectedToolState);
    const { propagatedUid } = useRecoilValue(replanningRunOutputsState);
    const setTab = useSetRecoilState(replanningRegisterTabState);
    const shown =
        useRecoilValue(replanningVisibleRegisterTabState) === 'visualization'
            ? 1
            : 0;
    useSelectRunRegistration();
    useRunLandmarkComparison();

    // The arrows, grid and heatmap belong to this step; leaving it turns them off so they do not linger on other steps.
    const resetDeformationOverlays = useResetDeformationOverlays();
    useEffect(
        () => () => resetDeformationOverlays(),
        [resetDeformationOverlays]
    );

    const canVisualize = !!propagatedUid;

    if (selectedTool === 'landmark') {
        return <LandmarkPanel />;
    }

    return (
        <Box height="100%" display="grid" gridTemplateRows="auto 1fr">
            <Tabs
                value={shown}
                onChange={(_, value) =>
                    setTab(value === 1 ? 'visualization' : 'process')
                }
                variant="fullWidth"
                sx={{
                    minHeight: 32,
                    '& .MuiTab-root': {
                        minHeight: 32,
                        fontSize: 11,
                        textTransform: 'none',
                        py: 0,
                    },
                }}
            >
                <Tab
                    label={intl.formatMessage({
                        id: 'replanning.register.process',
                    })}
                    data-testid="replanning-register-process-tab"
                />
                <Tab
                    label={
                        <Tooltip
                            title={
                                canVisualize
                                    ? ''
                                    : intl.formatMessage({
                                          id: 'replanning.register.visualizeBlocked',
                                      })
                            }
                        >
                            <Box component="span">
                                {intl.formatMessage({
                                    id: 'replanning.register.visualize',
                                })}
                            </Box>
                        </Tooltip>
                    }
                    disabled={!canVisualize}
                    data-testid="replanning-register-visualize-tab"
                />
            </Tabs>

            {shown === 0 ? (
                <Box minHeight={0} overflow="auto">
                    {propagatedUid ? (
                        <RegisterRunSummary />
                    ) : (
                        <Box px={1.5} pt={1.5} pb={1}>
                            <PropagationForm />
                        </Box>
                    )}
                </Box>
            ) : (
                <Box minHeight={0} overflow="auto">
                    <RegisterVisualizationPanel />
                </Box>
            )}
        </Box>
    );
}
